import { SiteUrls } from '@tableau/shared-urls';
import { IPromise } from 'angular';
import { call, put, select } from 'redux-saga/effects';
import { Messages } from '../../../../codegen/Localize';
import { assertUnreachable } from '../../../../ts/helpers/TypescriptHelpers';
import { Databases } from '../../../../ts/resources/Databases';
import { Tables } from '../../../../ts/resources/Tables';
import { ModalInstance, ReactModalService } from '../../../../ts/services/ReactModalService';
import { RemoveExternalContentAction } from '../../../actions/RemoveExternalContentAction';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { RemoveExternalContentDialogProps, removeExternalContentModalOptions } from '../../../content/RemoveExternalContentDialog';
import { siteUrlsSelector } from '../../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../../ServiceLocator';

export enum RemoveButtonClicked {
  submitted = 'submitted',
  cancelled = 'cancelled',
}

function isRemoveButtonClicked(value: any): value is RemoveButtonClicked {
  return Object.keys(RemoveButtonClicked).includes(value);
}

export function* handleRemoveExternalContent(action: RemoveExternalContentAction) {
  try {
    const siteUrls: SiteUrls = yield select(siteUrlsSelector);
    const result: RemoveButtonClicked | any = yield call(openRemoveExternalContentDialog, action);
    const maybeNewUrl = window.location.origin + getContentListUrl(action, siteUrls);

    if (isRemoveButtonClicked(result)) {
      switch (result) {
        case RemoveButtonClicked.cancelled: break;
        case RemoveButtonClicked.submitted: {
          if (window.location.href === maybeNewUrl) {
            window.location.reload();
          } else {
            window.location.href = maybeNewUrl;
          }
          break;
        }
        default: assertUnreachable(result);
      }
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function getContentListUrl(action: RemoveExternalContentAction, siteUrls: SiteUrls) {
  switch(action.item.contentType) {
    case 'database': return siteUrls.catalogDatabasesUrl();
    case 'table': return siteUrls.catalogTablesUrl();
    default: assertUnreachable(action.item);
  }
}

export function openRemoveExternalContentDialog(action: RemoveExternalContentAction): IPromise<any> {
  let modalInstance: ModalInstance;

  const removeExternalContent = () => {
    const item = action.item;
    switch(item.contentType) {
      case 'database': return ServiceLocator.get<Databases>('Databases').deleteDatabase(item.vizportalId, item.name);
      case 'table': return ServiceLocator.get<Tables>('Tables').deleteTable(item.vizportalId, item.name);
      default: assertUnreachable(item);
    }
  };

  const props: RemoveExternalContentDialogProps = {
    name: action.item.name,
    submit: () => removeExternalContent().then(() => modalInstance && modalInstance.close('submitted' as RemoveButtonClicked)),
    cancel: () => modalInstance && modalInstance.close('cancelled' as RemoveButtonClicked),
  };

  modalInstance = ReactModalService.open(removeExternalContentModalOptions(props));
  return modalInstance.result;
}
